<template>
  <div class="webhook-events">
    <div class="webhook-events__header">
      <h3 class="app-title webhook-events__title">Eventos de webhooks</h3>
      <AppButton variant="primary" outline :disabled="exporting" @click="exportEvents">
        {{ exporting ? 'Exportando...' : 'Exportar Excel' }}
      </AppButton>
    </div>

    <form class="webhook-events__filters app-card" @submit.prevent="applyFilters">
      <AppField label="Desde">
        <AppInput v-model="filters.startDate" type="date" />
      </AppField>
      <AppField label="Hasta">
        <AppInput v-model="filters.endDate" type="date" />
      </AppField>
      <AppField label="Estado">
        <AppSelect v-model="filters.status" :options="statusOptions" />
      </AppField>
      <AppField label="Origen">
        <AppSelect v-model="filters.source" :options="sourceOptions" />
      </AppField>
      <AppField label="Tienda" class="webhook-events__store-filter">
        <AppAutocomplete
          v-model="filters.storeId"
          :items="stores"
          item-text="name"
          item-value="id"
          :is-loading="loadingStores"
          placeholder="Todas las tiendas" />
      </AppField>
      <div class="webhook-events__filter-actions">
        <AppButton type="submit" variant="primary">Buscar</AppButton>
        <AppButton variant="secondary" outline @click="clearFilters">Limpiar</AppButton>
      </div>
    </form>

    <div class="app-card webhook-events__table">
      <AppTable
        :items="events"
        :fields="fields"
        :busy="loading"
        :show-empty="true"
        empty-text="No hay eventos para los filtros seleccionados."
        id="webhook-events-table"
        primary-key="id"
        responsive
        small
        hover>
        <template #cell(createdAt)="{ value }">
          {{ formatDate(value) }}
        </template>
        <template #cell(store)="{ item }">
          {{ item.store ? item.store.name : '—' }}
        </template>
        <template #cell(status)="{ value }">
          <span :class="['webhook-events__status', `webhook-events__status--${value}`]">
            {{ statusLabel(value) }}
          </span>
        </template>
        <template #cell(errorMessage)="{ value }">
          <span class="webhook-events__error" :title="value">{{ value || '' }}</span>
        </template>
        <template #cell(actions)="{ item }">
          <AppIconButton icon="visibility" title="Ver detalle" @click="openDetail(item)" />
        </template>
      </AppTable>

      <div class="webhook-events__pagination">
        <span class="webhook-events__total">{{ pagination.total }} evento(s)</span>
        <AppPagination
          :value="pagination.page"
          :total-rows="pagination.total"
          :per-page="limit"
          :disabled="loading"
          align="center"
          aria-controls="webhook-events-table"
          @change="goToPage" />
        <label class="webhook-events__page-size">
          Por página
          <AppSelect :value="limit" :options="pageSizes" @input="changeLimit" />
        </label>
      </div>
    </div>

    <AppModal
      :value="detailOpen"
      size="lg"
      :title="selectedEvent ? `Evento #${selectedEvent.id}` : 'Evento'"
      @input="detailOpen = $event">
      <WebhookEventDetail
        v-if="selectedEvent"
        :event="selectedEvent"
        @copy-error="makeDangerToast('No se pudo copiar el payload')" />
    </AppModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment-timezone'
import MakeToastMixin from '../mixins/MakeToastMixin.vue'
import AppAutocomplete from '../UI/AppAutocomplete.vue'
import AppButton from '../UI/AppButton.vue'
import AppField from '../UI/AppField.vue'
import AppIconButton from '../UI/AppIconButton.vue'
import AppInput from '../UI/AppInput.vue'
import AppModal from '../UI/AppModal.vue'
import AppPagination from '../UI/AppPagination.vue'
import AppSelect from '../UI/AppSelect.vue'
import AppTable from '../UI/AppTable.vue'
import WebhookEventDetail from './WebhookEventDetail.vue'
import {
  WEBHOOK_EVENT_PAGE_SIZES,
  WEBHOOK_EVENT_SOURCES,
  WEBHOOK_EVENT_STATUSES,
  buildWebhookEventQuery,
  emptyWebhookEventFilters,
  webhookEventStatusLabel
} from '@/utils/webhookEventUtils'

export default {
  name: 'ListWebhookEvents',
  components: {
    AppAutocomplete,
    AppButton,
    AppField,
    AppIconButton,
    AppInput,
    AppModal,
    AppPagination,
    AppSelect,
    AppTable,
    WebhookEventDetail
  },
  mixins: [MakeToastMixin],
  data () {
    return {
      events: [],
      stores: [],
      loading: false,
      loadingStores: false,
      exporting: false,
      filters: emptyWebhookEventFilters(),
      // Filtros con los que se hizo la última búsqueda (los usa la paginación).
      appliedFilters: emptyWebhookEventFilters(),
      limit: 50,
      pagination: { total: 0, page: 1, totalPages: 0 },
      detailOpen: false,
      selectedEvent: null,
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'createdAt', label: 'Recibido' },
        { key: 'source', label: 'Origen' },
        { key: 'eventType', label: 'Evento' },
        { key: 'store', label: 'Tienda' },
        { key: 'status', label: 'Estado' },
        { key: 'errorMessage', label: 'Error' },
        { key: 'actions', label: 'Acciones' }
      ],
      statusOptions: [{ value: '', text: 'Todos' }, ...WEBHOOK_EVENT_STATUSES],
      sourceOptions: [{ value: '', text: 'Todos' }, ...WEBHOOK_EVENT_SOURCES],
      pageSizes: WEBHOOK_EVENT_PAGE_SIZES
    }
  },
  async mounted () {
    await Promise.all([this.fetchStores(), this.fetchEvents(1)])
  },
  methods: {
    ...mapActions(['listWebhookEvents', 'exportWebhookEvents', 'listAllStoreIdName']),
    statusLabel: webhookEventStatusLabel,
    formatDate (value) {
      return value ? moment(value).tz('America/Santiago').format('DD-MM-YYYY HH:mm:ss') : '—'
    },
    async fetchStores () {
      this.loadingStores = true
      try {
        const stores = await this.listAllStoreIdName()
        this.stores = Array.isArray(stores) ? stores : []
      } finally {
        this.loadingStores = false
      }
    },
    async fetchEvents (page) {
      this.loading = true
      try {
        const params = buildWebhookEventQuery(this.appliedFilters, { page, limit: this.limit })
        const response = await this.listWebhookEvents({ params })
        this.events = response.data || []
        this.pagination = response.pagination || { total: 0, page: 1, totalPages: 0 }
      } catch (error) {
        this.makeDangerToast('No se pudieron cargar los eventos')
      } finally {
        this.loading = false
      }
    },
    applyFilters () {
      this.appliedFilters = { ...this.filters }
      this.fetchEvents(1)
    },
    clearFilters () {
      this.filters = emptyWebhookEventFilters()
      this.applyFilters()
    },
    goToPage (page) {
      this.fetchEvents(page)
    },
    changeLimit (value) {
      this.limit = Number(value)
      this.fetchEvents(1)
    },
    openDetail (event) {
      this.selectedEvent = event
      this.detailOpen = true
    },
    async exportEvents () {
      this.exporting = true
      try {
        const { page, limit, ...params } = buildWebhookEventQuery(this.appliedFilters)
        await this.exportWebhookEvents({ params })
      } catch (error) {
        this.makeDangerToast('No se pudo exportar')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.webhook-events {
  display: flex;
  flex-direction: column;
  gap: var(--app-spacing-md);
  padding: var(--app-spacing-md) 0;
}

.webhook-events__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--app-spacing-md);
  flex-wrap: wrap;
}

.webhook-events__title {
  margin-bottom: 0;
}

.webhook-events__filters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--app-spacing-sm) var(--app-spacing-md);
  align-items: end;
}

.webhook-events__store-filter {
  grid-column: span 2;
}

.webhook-events__filter-actions {
  display: flex;
  gap: var(--app-spacing-sm);
  padding-bottom: var(--app-spacing-md);
}

.webhook-events__status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--app-border-radius-lg);
  font-size: var(--app-font-size-sm);
  font-weight: 600;
  color: #fff;
  background-color: var(--app-icon-color);
  white-space: nowrap;
}

.webhook-events__status--processed {
  background-color: var(--app-success);
}

.webhook-events__status--failed {
  background-color: var(--app-danger);
}

.webhook-events__status--processing {
  background-color: var(--app-info);
}

.webhook-events__status--received {
  background-color: var(--app-warning);
}

.webhook-events__error {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.webhook-events__pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--app-spacing-md);
  padding-top: var(--app-spacing-md);
}

.webhook-events__total {
  font-size: var(--app-font-size-sm);
}

.webhook-events__page-size {
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: var(--app-spacing-sm);
  margin: 0;
  font-size: var(--app-font-size-sm);
}

@media (max-width: 768px) {
  .webhook-events__store-filter {
    grid-column: auto;
  }
}
</style>
